import { CanActivateFn, Router } from '@angular/router';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { UserStateService } from 'riterz-core';
import { filter, map, take } from 'rxjs';

/**
 * Requires the authenticated user's status to be `approved`.
 *
 * **Layer 2** — always placed after `loggedInGuard`. Waits for `currentUser`
 * to emit, then redirects based on status:
 * - `pending`  → `/pending-approval`
 * - `rejected` → `/registration-rejected`
 * - `approved` → allowed through
 *
 * SSR: returns `true` (user data unavailable server-side, prevents 504 timeouts).
 * OAuth redirect: allows through when token query params are present.
 *
 * Apply to every route that requires a fully approved account.
 * Do NOT apply to `/pending-approval` or `/registration-rejected` themselves
 * (those only need `loggedInGuard`).
 */
export const approvedGuard: CanActivateFn = (
  route,
  state,
  platformId = inject(PLATFORM_ID),
  router: Router = inject(Router),
  userStateService: UserStateService = inject(UserStateService),
) => {
  // Skip approval check on server — user data isn't available during SSR
  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  // Let OAuth redirects through — AppComponent will process tokens and redirect
  if (route.queryParams['refresh_token'] && route.queryParams['access_token']) {
    return true;
  }

  return userStateService.currentUser.pipe(
    filter(user => user !== null),
    take(1),
    map(user => {
      if (user.status === 'rejected') {
        router.navigate(['/registration-rejected']);
        return false;
      }

      if (user.status === 'pending') {
        router.navigate(['/pending-approval']);
        return false;
      }

      return true;
    })
  );
};
